import { useRef, useState } from 'react';

import Tippy from '@tippyjs/react';

import { TOOLTIP_TOUCH_HOLD } from 'lib/constants/misc';
import { cn, formatLargeNumber } from 'lib/helpers/utility';

import Icon from '../../Icon/Icon';
import Button from '../../Button/Button';

const TabItem = ({ item, active, disabled, onClick }) => {
  const [open, setOpen] = useState(false);
  const [heldWidth, setHeldWidth] = useState(null);
  const tabRef = useRef(null);
  const isPinned = active || open;

  // An inactive tab keeps no room for its menu: while pointed at or focused it holds its width and
  // the label truncates to make room, so the tabs after it never shift.
  const hold = () => {
    if (item.actions && !isPinned && heldWidth == null) setHeldWidth(tabRef.current?.offsetWidth);
  };
  const release = ({ relatedTarget }) => {
    const isStillInside = relatedTarget instanceof Node && tabRef.current?.contains(relatedTarget);
    if (!isStillInside) setHeldWidth(null);
  };
  const isHeld = !isPinned && heldWidth != null;

  return (
    <>
      {item.split && <span className="mx-1 h-4 w-px shrink-0 self-center bg-neutral-200" />}
      <div
        ref={tabRef}
        onPointerEnter={hold}
        onPointerLeave={release}
        onFocus={hold}
        onBlur={release}
        style={isHeld ? { width: heldWidth } : undefined}
        className={cn(
          'relative -mb-px flex h-full min-w-0 items-center border-0 border-b-2 border-solid transition-colors duration-150',
          active ? 'border-primary-500' : 'border-transparent hover:border-neutral-200'
        )}>
        <Tippy
          content={item.tooltip || item.label}
          className="tippy-dark"
          placement="bottom"
          delay={[400, 0]}
          touch={TOOLTIP_TOUCH_HOLD}>
          <button
            type="button"
            onClick={() => onClick(item)}
            disabled={disabled || item.disabled}
            aria-current={active ? 'true' : undefined}
            data-qa={item.label}
            className={cn(
              'flex h-full min-w-0 max-w-[200px] cursor-pointer items-center gap-1.5 border-0 bg-transparent px-2.5 text-sm transition-colors duration-150',
              active
                ? 'font-600 text-primary-600'
                : 'font-500 text-neutral-600 hover:text-neutral-900'
            )}>
            <span className="truncate">{item.label}</span>
            {item.count != null && (
              <span
                className={cn(
                  'flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-600',
                  active ? 'bg-primary-50 text-primary-600' : 'bg-neutral-100 text-neutral-600'
                )}>
                {formatLargeNumber(item.count)}
              </span>
            )}
            {item.default && (
              <Icon icon="star-1-filled" size={12} color="warning" aria-label="Default view" />
            )}
          </button>
        </Tippy>
        {item.actions && (isPinned || isHeld) && (
          <Button.Dropdown
            icon="context-menu-vertical"
            aria-label={`${item.label} view options`}
            items={item.actions}
            iconColor="neutral"
            iconsShade={600}
            iconIsStroke
            open={open}
            setOpen={setOpen}
            variant="tertiary"
            size="xs"
            className="mr-1 !size-6 !border-0 !bg-transparent !p-0 hover:!bg-neutral-75"
          />
        )}
      </div>
    </>
  );
};

export default TabItem;
